import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Skill {
    _id: string;
    name: string;
    description?: string;
    usageCount: number;
}

interface SkillsDropdownProps {
    selectedSkills: string[];
    onSkillsChange: (skillIds: string[]) => void;
    placeholder?: string;
    maxSkills?: number;
    showSearch?: boolean;
    className?: string;
    disabled?: boolean;
}

const SkillsDropdown: React.FC<SkillsDropdownProps> = ({
    selectedSkills,
    onSkillsChange,
    placeholder = "Select skills...",
    maxSkills = 10,
    showSearch = true,
    className = "",
    disabled = false
}) => {
    const dispatch = useAppDispatch();
    const [skills, setSkills] = useState<Skill[]>([]);
    const [filteredSkills, setFilteredSkills] = useState<Skill[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);


    const fetchSkills = useCallback(async () => {
        try {
            setLoading(true);
            // If there's a search term, use search endpoint, otherwise get all skills
            const endpoint = searchTerm && searchTerm.length >= 2
                ? `/skills/search?q=${searchTerm}`
                : '/skills/all';
            const response = await api.get(endpoint);
            setSkills(response.data.data);
        } catch (error: any) {
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch skills',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch, searchTerm]);

    useEffect(() => {
        fetchSkills();
    }, [fetchSkills]);

    useEffect(() => {
        let filtered = skills;

        // Filter by search term
        if (searchTerm) {
            const searchRegex = new RegExp(searchTerm, 'i');
            filtered = filtered.filter(skill =>
                skill.name.match(searchRegex) ||
                (skill.description && skill.description.match(searchRegex))
            );
        }


        // Exclude already selected skills
        filtered = filtered.filter(skill => !selectedSkills.includes(skill._id));

        setFilteredSkills(filtered);
    }, [skills, searchTerm, selectedSkills]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSkillSelect = (skillId: string) => {
        if (selectedSkills.length >= maxSkills) {
            dispatch(showToast({
                message: `You can select maximum ${maxSkills} skills`,
                type: 'warning'
            }));
            return;
        }

        onSkillsChange([...selectedSkills, skillId]);
        setSearchTerm('');
    };

    const handleSkillRemove = (skillId: string) => {
        onSkillsChange(selectedSkills.filter(id => id !== skillId));
    };

    const getSelectedSkillNames = () => {
        return selectedSkills.map(skillId => {
            const skill = skills.find(s => s._id === skillId);
            return skill ? skill.name : skillId;
        });
    };


    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Selected Skills Display */}
            <div className="min-h-[42px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 p-2 flex flex-wrap gap-1">
                {getSelectedSkillNames().map((skillName, index) => (
                    <span
                        key={selectedSkills[index]}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    >
                        {skillName}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => handleSkillRemove(selectedSkills[index])}
                                className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
                            >
                                <XMarkIcon className="w-3 h-3" />
                            </button>
                        )}
                    </span>
                ))}
                {selectedSkills.length === 0 && (
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                        {placeholder}
                    </span>
                )}
                {!disabled && (
                    <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        className="ml-auto inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-80 overflow-hidden">
                    {/* Search */}
                    {showSearch && (
                        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="relative">
                                <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search skills..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}


                    {/* Skills List */}
                    <div className="max-h-48 overflow-y-auto">
                        {loading ? (
                            <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                                Loading skills...
                            </div>
                        ) : filteredSkills.length === 0 ? (
                            <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                                No skills found
                            </div>
                        ) : (
                            filteredSkills.map((skill) => (
                                <button
                                    key={skill._id}
                                    type="button"
                                    onClick={() => handleSkillSelect(skill._id)}
                                    className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {skill.name}
                                            </div>
                                            {skill.description && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {skill.description}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <span className="text-xs text-gray-400">
                                                {skill.usageCount}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                            {selectedSkills.length} of {maxSkills} skills selected
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SkillsDropdown;
